/**
 * Rapprochement bancaire CODA — importer un relevé, puis placer ce qui reste.
 *
 * Page **ajoutée**, qui ne remplace rien. Les écrans existants
 * (`/admin/payments`, `/admin/bank-transactions`, `/admin/financial-overview`)
 * continuent de fonctionner sur l'ancien circuit, intact.
 *
 * Deux onglets sur une seule page, parce que le geste est enchaîné : on importe
 * les sept fichiers de la semaine, puis on traite dans la foulée les paiements
 * que l'automatique n'a pas placés. Deux pages auraient forcé un aller-retour.
 *
 * Le compteur de la file se met à jour après un import, de sorte que l'onglet
 * annonce le travail qui attend sans qu'on ait à y aller voir.
 */

import { useCallback, useEffect, useState } from 'react';
import { Banknote, FileUp, HandCoins, History, Landmark, ListChecks } from 'lucide-react';
import { Toaster } from 'react-hot-toast';
import clsx from 'clsx';
import CodaImportPanel from './components/CodaImportPanel';
import ReconciliationQueue from './components/ReconciliationQueue';
import AllocationJournal from './components/AllocationJournal';
import DepensesAPlacer from './components/DepensesAPlacer';
import DemandesDeRemboursement from './components/DemandesDeRemboursement';
import { messageErreur } from '../../lib/payments/erreur';
import { countPendingPayments } from '../../lib/payments/actions.supabase';

type Onglet = 'import' | 'file' | 'depenses' | 'demandes' | 'journal';

export default function AdminCodaReconciliationPage() {
  const [onglet, setOnglet] = useState<Onglet>('import');
  /*
    Une recherche transmise a la file par l'onglet « Rapproches ».

    Le 19 septembre 2026, un versement de 110,00 EUR cherche la-bas n'y etait
    pas : il attendait dans la file, et « Rapproches » ne montre que ce qui a
    ete decide. Le journal dit desormais combien de mouvements attendent — et
    ce champ porte la recherche jusqu'a la file, pour que le clic tombe sur la
    ligne cherchee et non sur 3 028 lignes.
  */
  const [rechercheDeLaFile, setRechercheDeLaFile] = useState<string | undefined>(undefined);
  const [rafraichir, setRafraichir] = useState(0);
  const [enAttente, setEnAttente] = useState<{ nombre: number } | null>(null);
  /* Le compteur du SORTANT, lu separement : les deux files ne contiennent pas
     la meme chose et n'ont pas la meme taille. Melanger leurs compteurs ferait
     croire a une file unique. */
  const [depensesEnAttente, setDepensesEnAttente] = useState<number | null>(null);
  const [demandesEnAttente, setDemandesEnAttente] = useState<number | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  /**
   * Le compteur de la file, lu séparément du contenu.
   *
   * ## Le défaut que ce commentaire décrivait sans le voir
   *
   * Il annonçait « une requête d'agrégat, pas la file entière ». C'était faux :
   * la requête sélectionnait `unallocated_cents` sur toutes les lignes et en
   * prenait la longueur. PostgREST plafonne une réponse à **mille lignes**, si
   * bien que le badge affichait `1000` — pas mille lignes à traiter, mais son
   * propre plafond.
   *
   * Mesuré en production le 6 septembre 2026 : la file compte **8 715 lignes**
   * pour 245 058,18 €. Le badge en annonçait 1 000, et personne ne pouvait le
   * savoir en le lisant.
   *
   * `countPendingPayments` demande maintenant un vrai `count` et ne rapatrie
   * aucune ligne. Le MONTANT, lui, n'est plus affiché ici : PostgREST ne sait
   * pas l'agréger, et additionner la page reçue ferait passer un total partiel
   * pour le total. **Mieux vaut ne pas donner un chiffre que d'en donner un
   * faux** — c'est la leçon du « Reste à payer » lu dans l'URL.
   */
  const compterFile = useCallback(async () => {
    try {
      const { nombre } = await countPendingPayments();
      setErreur(null);
      setEnAttente({ nombre });
    } catch (e) {
      // Une erreur ici était avalée en silence : le compteur restait vide et
      // rien ne disait pourquoi. Sur un écran qui parle d'argent, un chiffre
      // absent doit s'expliquer.
      setErreur(messageErreur(e, 'Compteur de la file indisponible'));
    }
  }, []);

  useEffect(() => { void compterFile(); }, [compterFile, rafraichir]);

  /*
    Stable, et pas une fonction ecrite dans le JSX.

    Une fonction recreee a chaque rendu redeclenche l'effet de la file, qui
    recharge, qui rappelle ce compteur, qui redessine la page : la file se
    rechargeait sans fin sans jamais s'afficher. La file se protege aussi de son
    cote par une reference, mais la corriger ici est la vraie reparation.
  */
  const noterCompte = useCallback((nombre: number) => {
    setEnAttente({ nombre });
  }, []);

  const apresImport = useCallback(() => {
    setRafraichir((n) => n + 1);
    // On bascule sur la file : c'est la suite naturelle du geste, et c'est là
    // que se trouve le travail que l'import vient de mettre de côté.
    setOnglet('file');
  }, []);

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />

      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-gray-900">
          <Landmark className="h-6 w-6 text-indigo-600" />
          Rapprochement bancaire
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-gray-600">
          Lecture des relevés CODA d'ING, rapprochement automatique avec les factures, et
          traitement de ce qui demande une décision. Les écrans de paiements existants ne
          sont pas touchés.
        </p>
      </header>

      <nav className="flex gap-1 border-b border-gray-200">
        <BoutonOnglet
          actif={onglet === 'import'}
          onClick={() => setOnglet('import')}
          icone={<FileUp className="h-4 w-4" />}
        >
          Importer un relevé
        </BoutonOnglet>
        <BoutonOnglet
          actif={onglet === 'file'}
          onClick={() => setOnglet('file')}
          icone={<ListChecks className="h-4 w-4" />}
          badge={enAttente?.nombre}
          titre={
            enAttente
              ? enAttente.nombre === 0
                ? 'rien à placer'
                : `${enAttente.nombre} paiement(s) à placer`
              : undefined
          }
        >
          File de rapprochement
        </BoutonOnglet>
        <BoutonOnglet
          actif={onglet === 'depenses'}
          onClick={() => setOnglet('depenses')}
          icone={<Banknote className="h-4 w-4" />}
          badge={depensesEnAttente ?? undefined}
          titre={
            depensesEnAttente !== null
              ? depensesEnAttente === 0
                ? 'rien a placer'
                : `${depensesEnAttente} depense(s) a placer`
              : undefined
          }
        >
          Dépenses à placer
        </BoutonOnglet>
        <BoutonOnglet
          actif={onglet === 'demandes'}
          onClick={() => setOnglet('demandes')}
          icone={<HandCoins className="h-4 w-4" />}
          badge={demandesEnAttente ?? undefined}
          titre={
            demandesEnAttente !== null
              ? demandesEnAttente === 0
                ? 'aucune demande'
                : `${demandesEnAttente} demande(s) a virer`
              : undefined
          }
        >
          Demandes de remboursement
        </BoutonOnglet>
        <BoutonOnglet
          actif={onglet === 'journal'}
          onClick={() => setOnglet('journal')}
          icone={<History className="h-4 w-4" />}
        >
          Rapprochés
        </BoutonOnglet>
      </nav>

      {erreur && (
        <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{erreur}</p>
      )}

      {onglet === 'import' && <CodaImportPanel onApplied={apresImport} />}
      {onglet === 'file' && (
        <ReconciliationQueue
          rafraichir={rafraichir}
          // La recherche vient parfois de l'onglet « Rapproches » : on y
          // cherchait un versement qui attendait ici. Le clic doit arriver avec
          // la recherche deja faite, sinon il depose l'administration devant
          // 3 028 lignes et la meme question.
          rechercheInitiale={rechercheDeLaFile}
          // La file est la seule a savoir ce qu'elle contient : elle remonte son
          // compte, le badge suit. La page comptait de son cote et seulement
          // apres un import, si bien que le badge restait a 1 sur une file vide.
          onCount={noterCompte}
        />
      )}
      {onglet === 'depenses' && (
        <DepensesAPlacer rafraichir={rafraichir} onCount={setDepensesEnAttente} />
      )}
      {onglet === 'demandes' && (
        <DemandesDeRemboursement rafraichir={rafraichir} onCount={setDemandesEnAttente} />
      )}
      {onglet === 'journal' && (
        <AllocationJournal
          rafraichir={rafraichir}
          onVoirDansLaFile={(r) => { setRechercheDeLaFile(r); setOnglet('file'); }}
        />
      )}
    </div>
  );
}

function BoutonOnglet({
  actif, onClick, icone, badge, titre, children,
}: {
  actif: boolean;
  onClick: () => void;
  icone: React.ReactNode;
  badge?: number;
  titre?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={titre}
      className={clsx(
        'inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
        actif
          ? 'border-indigo-600 text-indigo-700'
          : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900',
      )}
    >
      {icone}
      {children}
      {badge !== undefined && badge > 0 && (
        <span className={clsx(
          'rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums',
          actif ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-700',
        )}>
          {badge}
        </span>
      )}
    </button>
  );
}
